"use client";

import useEmblaCarousel from "embla-carousel-react";
import Image from "next/image";
import { useCallback, useEffect, useState } from "react";
import { Lqip } from "@/components/ui/Lqip";
import { Scrollbar } from "@/components/ui/Scrollbar";

type MediaItem = {
	href: string;
	alt?: string;
	/** Tiny placeholder source for blur-up backdrop. */
	lqip?: string;
	/**
	 * Lower-res variant (Centra `standard`, 600x800) used for the thumbnail
	 * strip so the small thumbs don't pull the full-size `href`.
	 */
	thumb?: string;
};

interface ImagesProps {
	media: MediaItem[];
	productName: string;
}

const Slide = ({
	item,
	productName,
	index,
	priority,
}: {
	item: MediaItem;
	productName: string;
	index: number;
	priority: boolean;
}) => (
	<div className="relative aspect-3/4 min-w-0 shrink-0 grow-0 basis-full overflow-hidden bg-background-bg-image">
		{item.lqip && <Lqip src={item.lqip} loading={priority ? "eager" : "lazy"} />}
		<Image
			quality={90}
			src={item.href}
			alt={item.alt || productName}
			fill
			priority={priority}
			fetchPriority={priority ? "high" : "auto"}
			sizes="(min-width: 1024px) 58vw, 100vw"
			className="object-cover"
			data-index={index}
		/>
	</div>
);

const ThumbSlide = ({
	item,
	productName,
	index,
	isActive,
	onClick,
}: {
	item: MediaItem;
	productName: string;
	index: number;
	isActive: boolean;
	onClick: () => void;
}) => (
	<button
		type="button"
		onClick={onClick}
		className="group/thumb relative aspect-130/174 min-w-0 shrink-0 grow-0 basis-[calc(25%-6px)] cursor-pointer overflow-hidden bg-background-bg-image"
	>
		<div
			className={
				isActive
					? "absolute inset-0"
					: "absolute inset-0 opacity-75 transition-opacity duration-200 ease-out group-hover/thumb:opacity-100"
			}
		>
			{item.lqip && <Lqip src={item.lqip} loading="lazy" durationMs={150} />}
			<Image
				src={item.thumb ?? item.href}
				alt={item.alt || `${productName} ${index + 1}`}
				fill
				quality={75}
				sizes="(min-width: 1024px) 160px, 25vw"
				className="object-cover"
			/>
		</div>
		<div
			className={`absolute inset-0 border transition-opacity duration-200 ${isActive ? "opacity-100 border-border-dark bg-overlay-3" : "opacity-0 border-transparent"}`}
		/>
	</button>
);

export const Images = ({ media, productName }: ImagesProps) => {
	if (media.length === 0) return null;

	if (media.length === 1) {
		return <Slide item={media[0]} productName={productName} index={0} priority />;
	}

	return <ImageCarousel media={media} productName={productName} />;
};

const THUMB_SLIDE_SPACING = 8; // gap in px matching gap-grid-component-spacing-2xs

const ImageCarousel = ({ media, productName }: ImagesProps) => {
	const [selectedIndex, setSelectedIndex] = useState(0);
	const [mainRef, mainApi] = useEmblaCarousel({ loop: false });
	const [thumbRef, thumbApi] = useEmblaCarousel({
		containScroll: "keepSnaps",
		dragFree: true,
		slidesToScroll: 1,
		align: "start",
	});

	// Sync: when main carousel changes, update selected + scroll thumb into view
	const onMainSelect = useCallback(() => {
		if (!mainApi || !thumbApi) return;
		const index = mainApi.selectedScrollSnap();
		setSelectedIndex(index);
		thumbApi.scrollTo(Math.max(0, index - 1));
	}, [mainApi, thumbApi]);

	// Sync: clicking a thumbnail scrolls the main carousel
	const onThumbClick = useCallback(
		(index: number) => {
			mainApi?.scrollTo(index);
		},
		[mainApi],
	);

	useEffect(() => {
		if (!mainApi) return;
		mainApi.on("select", onMainSelect);
		onMainSelect();
		return () => {
			mainApi.off("select", onMainSelect);
		};
	}, [mainApi, onMainSelect]);

	const scrollPrev = useCallback(() => {
		mainApi?.scrollPrev();
	}, [mainApi]);

	const scrollNext = useCallback(() => {
		mainApi?.scrollNext();
	}, [mainApi]);

	return (
		<div className="flex flex-col gap-grid-component-spacing-2xs">
			{/* Main image carousel */}
			<div ref={mainRef} className="overflow-hidden">
				<div className="flex">
					{media.map((item, index) => (
						<Slide
							key={index}
							item={item}
							productName={productName}
							index={index}
							priority={index === 0}
						/>
					))}
				</div>
			</div>

			{/* Thumbnail carousel — desktop only, 4 visible at a time */}
			<div ref={thumbRef} className="hidden overflow-hidden lg:block">
				<div className="flex" style={{ gap: THUMB_SLIDE_SPACING }}>
					{media.map((item, index) => (
						<ThumbSlide
							key={index}
							item={item}
							productName={productName}
							index={index}
							isActive={selectedIndex === index}
							onClick={() => onThumbClick(index)}
						/>
					))}
				</div>
			</div>

			{/* Scrollbar navigation */}
			<Scrollbar
				current={selectedIndex + 1}
				total={media.length}
				onPrev={scrollPrev}
				onNext={scrollNext}
			/>
		</div>
	);
};
